'use client'
import { useMemo } from "react";
import Link from "next/link";
import useMount from "react-use/lib/useMount";
import { usePathname } from "next/navigation";
import styles from './categorySearch.module.css';
import PictureCard from 'components/_styled/pictureCard.module.css'
import logPageView from "lib/ga/pageView";
import { CategoryPagesProps, ResultThumbnail } from "lib/types";
import interpretThumbnail from "lib/interpretThumbnail";
import LazyImage from "components/LazyImage";
import SearchInput from "./search";


type image = {
  format: object
}

  const getThumbnail = (rawResult: {images: image[]}, searchType: string, categoryCode: string | undefined) => {
    let images: image[] = rawResult.images || [];
    const thumb: ResultThumbnail = {
      origin: '',
      default:
        searchType === "SCHEME" && categoryCode === "SC"
          ? "fallback-images/scheme-sc-fullback.jpeg"
          : "fallback-images/" + categoryCode?.toLowerCase() + "-fullback.jpeg",
      sizes: [200, 400, 600, 800],
    };

    // get the real one
    if (images && images.length > 0) {
      thumb.origin = "this";
      Object.keys(images[0].format)
        .map((k) => ({ ...images[0].format[k], file_type: k.endsWith('wp') ? 'webp' : 'jpg' }))
        .forEach((med) => {
          if (thumb.sizes.includes(med.width)) {
            if (med.file_type in thumb) {
              thumb[med.file_type][med.width] = med.uri;
            } else {
              thumb[med.file_type] = {
                [med.width]: med.uri,
              };
            }
          }
        });

      if ("jpg" in thumb) {
        thumb.default = thumb.jpg?.[200];
      } else if ("png" in thumb) thumb.default = thumb.png?.[200];
    }

    return thumb;
  };


const CategorySearch = ({ searchType, searchCriteria, categoryCode, featuredResults, searchInputType, forSale = false, heading, subHeading, nameLookUp, propertyTitle, searchTerms = [], heroImage = '/images/categorySearch/For_sale_hero.webp' }: CategoryPagesProps) => {

  let router = usePathname();
  const links = [
    { url: '/retail-parks', name: 'Retail Parks' },
    { url: '/shopping-centres', name: 'Shopping Centers' },
    { url: '/leisure-schemes', name: 'Leisure Parks' },
    { url: '/retail-centres', name: 'Retail Centres' }
  ];

  useMount(() => {
    logPageView({ url: window.location.pathname + window.location.search });
  });

  return (
    <div>
      <div className={styles.search}>
        <div className={styles.searchInner}>
          <h1>{heading}</h1>
          <p dangerouslySetInnerHTML={{ __html: subHeading }} />
          <SearchInput nameLookUp={nameLookUp} forSale={forSale} categoryUrl={router?.replace("/", "")} type={searchInputType} category={categoryCode} placeholder={`Begin typing the name of the ${heading.toLocaleLowerCase()}`} />
        </div>
        <div className={styles.heroImage} style={{ background: `url(${heroImage}) center center no-repeat`, backgroundSize: 'cover' }} />
      </div>

      {searchType !== 'PROPERTY' && (
        <div className={styles.container}>
          <h2>People are also searching</h2>
          <div className={styles.otherLinks}>
            {links.map((link) => {
              if (link.url !== router) {
                return (
                  <Link key={link.url} href={link.url} passHref prefetch={false}>
                    {link.name}
                  </Link>
                )
              }
            })}
          </div>
        </div>
      )}

      {Object.keys(featuredResults).length > 0 && (
        <div className={searchType !== 'PROPERTY' ? `${styles.container} ${styles.marginBottom}` : styles.container}>
          <h2>{propertyTitle}</h2>
          <div className={styles.featuredList}>
            {Object.keys(featuredResults).map((propertyId) => {
              const { id, full_name, name, uri, category_code, line1, town_name, postcode, url_fragment } = featuredResults[propertyId]._source;
              const address = [town_name, postcode].filter(Boolean).join(", ");
              const thumbnailProps = useMemo(() => {
                return interpretThumbnail(
                  getThumbnail(featuredResults[propertyId]._source, searchType, category_code), full_name ? full_name : address, category_code, searchType, "(min-width:80rem) 40vw, 20vw", 400);
              }, [full_name ? full_name : name]);
              return (
                <Link key={id} href={searchType === 'PROPERTY' ? `/property/${url_fragment ? url_fragment : id}` : `/scheme/${uri ? `${uri}-${id}` : id}`} passHref prefetch={false} legacyBehavior>
                  <a className={PictureCard.PictureCard_background}>
                    <div className={`${styles.rounded} ${PictureCard.PictureCard__Inner}`}>
                      <p className={PictureCard.PictureCard__Upper}>{full_name ? full_name : name}</p>
                      <h3 className={PictureCard.PictureCard__Lower} >{address}</h3>
                    </div>
                    <LazyImage {...thumbnailProps} radius={true} sizes={"(min-width:80rem) 40vw, 20vw"} className={styles.rounded} />
                  </a>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {searchTerms.length > 0 && (
        <div className={`${styles.container} ${styles.otherSearch}`}>
          <h2>People are also searching</h2>
          <ul>
            {searchTerms.map((term, index) => {
              return (
                <li key={index}>{term}</li>
              )
            })}
          </ul>
        </div>
      )}

    </div>
  )
};

export default CategorySearch;
